/**
 * 移动端环境检测工具
 * 检测是否在移动端，是否在钱包内浏览器中，以及支持的钱包类型
 */

import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("lib/utils/mobile-detection");

// 移动端钱包环境检测结果
export interface MobileWalletEnvironment {
	isMobile: boolean;
	isInWalletBrowser: boolean;
	walletType: string | null;
	supportedWallets: string[];
	userAgent: string;
}

// 已知的移动端钱包标识
const WALLET_USER_AGENTS = {
	okx: ["OKApp", "OKXWallet"],
	binance: ["BinanceApp", "Binance", "BNBAPP", "binance"],
	unisat: ["UniSat"],
	xverse: ["Xverse", "XverseWallet"],
	phantom: ["Phantom"],
	trust: ["Trust"],
	metamask: ["MetaMaskMobile"],
	coinbase: ["CoinbaseWallet"],
	imtoken: ["imToken"],
	tokenpocket: ["TokenPocket"],
} as const;

// 移动端浏览器标识
const MOBILE_USER_AGENTS = [
	"Android",
	"iPhone",
	"iPad",
	"iPod",
	"BlackBerry",
	"Windows Phone",
	"Mobile",
	"webOS",
	"Opera Mini",
	"IEMobile",
];

/**
 * 检测是否为移动端设备
 */
export function isMobileDevice(): boolean {
	if (typeof window === "undefined") return false;

	const userAgent = navigator.userAgent;

	// 检查用户代理字符串
	const isMobileUA = MOBILE_USER_AGENTS.some((mobile) =>
		userAgent.includes(mobile),
	);

	// iPadOS 在桌面模式下可能伪装成 Mac，需要额外识别
	const isTouchMac =
		navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;

	return isMobileUA || isTouchMac;
}

/**
 * 检测当前在哪个钱包浏览器中
 */
export function detectWalletBrowser(): string | null {
	if (typeof window === "undefined") return null;

	const userAgent = navigator.userAgent;

	// 特殊处理TokenPocket - 大小写不敏感
	if (userAgent.toLowerCase().includes("tokenpocket")) {
		return "tokenpocket";
	}

	for (const [walletName, identifiers] of Object.entries(WALLET_USER_AGENTS)) {
		if (identifiers.some((identifier) => userAgent.includes(identifier))) {
			return walletName;
		}
	}

	return null;
}

/**
 * 检测移动端可用的钱包
 */
export function getAvailableMobileWallets(): string[] {
	if (typeof window === "undefined") return [];

	const availableWallets: string[] = [];

	// 检查 window 对象中的钱包注入
	if ((window as any).okxwallet?.bitcoin) {
		availableWallets.push("okx");
	}

	// Binance 钱包检测 - 只检测支持 Bitcoin 的 Provider
	// 注意：Chrome 扩展不支持 Bitcoin，只有 Binance App 内置浏览器支持
	const hasBinanceBitcoinSupport = (() => {
		// 方式1: 原生 Binance Web3 Wallet Bitcoin Provider (App内浏览器)
		if ((window as any).binancew3w?.bitcoin) return true;
		// 方式2: UniSat 兼容模式
		if ((window as any).unisat?.isBinance) return true;
		return false;
	})();

	if (hasBinanceBitcoinSupport) {
		availableWallets.push("binance");
	}

	// UniSat 钱包检测 - 排除 Binance 兼容模式
	if ((window as any).unisat && !(window as any).unisat?.isBinance) {
		availableWallets.push("unisat");
	}

	if ((window as any).XverseProviders?.BitcoinProvider) {
		availableWallets.push("xverse");
	}

	if ((window as any).phantom?.bitcoin) {
		availableWallets.push("phantom");
	}

	// TokenPocket检测 - 多种可能的注入对象
	const windowObj = window as any;
	const hasTokenPocketInjection =
		windowObj.tokenpocket?.bitcoin ||
		windowObj.tp?.btc ||
		windowObj.tp ||
		windowObj.TokenPocket;

	// 搜索包含isTokenPocket属性的对象
	let hasTokenPocketFlag = false;
	for (const key in windowObj) {
		try {
			// 添加try-catch来捕获跨域frame访问错误
			if (
				windowObj[key] &&
				typeof windowObj[key] === "object" &&
				windowObj[key].isTokenPocket
			) {
				hasTokenPocketFlag = true;
				break;
			}
		} catch (e) {}
	}

	// 如果在TokenPocket浏览器中或有注入对象
	const isInTokenPocket = navigator.userAgent
		.toLowerCase()
		.includes("tokenpocket");

	if (hasTokenPocketInjection || isInTokenPocket || hasTokenPocketFlag) {
		availableWallets.push("tokenpocket");
	}

	// 在移动端，优先检测当前浏览器环境
	const currentWallet = detectWalletBrowser();
	if (currentWallet && !availableWallets.includes(currentWallet)) {
		availableWallets.unshift(currentWallet); // 将当前钱包浏览器放在首位
	}

	return availableWallets;
}

/**
 * 检测是否在钱包内浏览器中
 */
export function isInWalletBrowser(): boolean {
	return detectWalletBrowser() !== null;
}

/**
 * 获取完整的移动端钱包环境信息
 */
export function getMobileWalletEnvironment(): MobileWalletEnvironment {
	const isMobile = isMobileDevice();
	const isInWallet = isInWalletBrowser();
	const walletType = detectWalletBrowser();
	const supportedWallets = getAvailableMobileWallets();
	const userAgent = typeof window !== "undefined" ? navigator.userAgent : "";

	return {
		isMobile,
		isInWalletBrowser: isInWallet,
		walletType,
		supportedWallets,
		userAgent,
	};
}

/**
 * 生成钱包深链接
 * 根据最新文档更新深链接格式
 */
export function generateWalletDeepLink(
	walletType: string,
	dappUrl: string,
): string {
	const encodedUrl = encodeURIComponent(dappUrl);

	switch (walletType) {
		case "okx":
			// OKX 官方推荐通过 download 通用链接包装 deeplink，兼容外部浏览器/PWA 拉起 App
			return `https://web3.okx.com/download?deeplink=${encodeURIComponent(
				`okx://wallet/dapp/url?dappUrl=${encodedUrl}`,
			)}`;

		case "binance":
			// Binance App 深链接 - 打开应用主页
			// 注意：Binance 没有提供直接打开 DApp 浏览器的深链接
			// 用户需要手动导航到 Web3 钱包 -> 浏览器
			return `bnc://app.binance.com`;

		case "unisat":
			// UniSat钱包深链接格式
			return `unisat://dapp?url=${encodedUrl}`;

		case "xverse":
			// Xverse使用Universal Links (最新文档格式)
			return `https://connect.xverse.app/browser?url=${encodedUrl}`;

		case "phantom":
			// 注意：Phantom的深链接目前只支持Solana，Bitcoin功能需要直接在APP中访问
			// 使用Universal Links Browse格式，但可能不完全支持Bitcoin操作
			return `https://phantom.app/ul/browse/${encodedUrl}?ref=${encodedUrl}`;

		case "trust":
			return `https://link.trustwallet.com/open_url?coin_id=0&url=${encodedUrl}`;

		case "tokenpocket": {
			// TokenPocket钱包深链接格式 - 使用tpdapp://open协议
			// 需要构建params对象并进行URL编码
			const params = {
				url: dappUrl, // 使用原始URL，不需要预先编码
				chain: "BTC", // Bitcoin链
				source: "odin.baby",
			};
			const paramsStr = encodeURIComponent(JSON.stringify(params));
			return `tpdapp://open?params=${paramsStr}`;
		}

		default:
			return dappUrl;
	}
}

/**
 * 尝试打开钱包深链接
 * 改进错误处理和用户体验
 */
export function openWalletDeepLink(
	walletType: string,
	fallbackAction?: () => void,
	options: {
		dappUrl?: string;
		timeout?: number;
		showFallbackMessage?: boolean;
	} = {},
): Promise<boolean> {
	return new Promise((resolve) => {
		if (typeof window === "undefined") {
			resolve(false);
			return;
		}

		const {
			dappUrl = window.location.href,
			timeout = 3000,
			showFallbackMessage = true,
		} = options;
		const deepLink = generateWalletDeepLink(walletType, dappUrl);

		uiLog.info(`🔗 尝试打开${walletType}钱包深链接:`, deepLink);

		// 记录页面焦点状态，用于检测是否成功跳转
		let pageHidden = false;
		const handleVisibilityChange = () => {
			if (document.hidden) {
				pageHidden = true;
				resolve(true);
			}
		};

		// 监听页面可见性变化
		document.addEventListener("visibilitychange", handleVisibilityChange);

		// 尝试打开深链接
		try {
			if (
				walletType === "xverse" ||
				walletType === "trust" ||
				walletType === "phantom"
			) {
				// Xverse、Trust和Phantom使用Universal Links，直接跳转
				window.location.href = deepLink;
			} else if (walletType === "tokenpocket") {
				// TokenPocket使用tpdapp://协议，需要特殊处理
				// 在移动端浏览器中，直接设置location.href
				window.location.href = deepLink;

				// 备用方案：如果上面的方法不起作用，尝试使用iframe
				setTimeout(() => {
					if (!pageHidden) {
						const iframe = document.createElement("iframe");
						iframe.style.display = "none";
						iframe.src = deepLink;
						document.body.appendChild(iframe);

						setTimeout(() => {
							document.body.removeChild(iframe);
						}, 1000);
					}
				}, 100);
			} else {
				// 其他钱包使用隐藏链接方式
				const link = document.createElement("a");
				link.href = deepLink;
				link.style.display = "none";
				document.body.appendChild(link);

				link.click();
				document.body.removeChild(link);
			}

			// 设置超时检查
			setTimeout(() => {
				document.removeEventListener(
					"visibilitychange",
					handleVisibilityChange,
				);

				if (!pageHidden) {
					uiLog.warn(`⚠️ ${walletType}钱包深链接可能失败，未检测到页面跳转`);

					if (showFallbackMessage && fallbackAction) {
						fallbackAction();
					}
					resolve(false);
				}
			}, timeout);
		} catch (error) {
			uiLog.error("❌ 深链接打开失败:", error);
			document.removeEventListener("visibilitychange", handleVisibilityChange);

			if (fallbackAction) {
				fallbackAction();
			}
			resolve(false);
		}
	});
}

/**
 * 检测是否支持钱包连接
 */
export function canConnectWallet(walletType: string): boolean {
	if (!isMobileDevice()) {
		return true; // PC端通过扩展程序
	}

	// 移动端检查是否在对应钱包浏览器中或有注入对象
	const currentWallet = detectWalletBrowser();
	const availableWallets = getAvailableMobileWallets();

	return currentWallet === walletType || availableWallets.includes(walletType);
}

/**
 * 检测 Binance Wallet Chrome 扩展是否安装（不一定支持 Bitcoin）
 */
export function isBinanceExtensionInstalled(): boolean {
	if (typeof window === "undefined") return false;

	try {
		// Chrome 扩展会注入 binancew3w 对象，但可能不支持 bitcoin
		return !!(window as any).binancew3w?.isExtension;
	} catch (error) {
		return false;
	}
}

/**
 * 检测 Binance Wallet 是否支持 Bitcoin
 */
export function isBinanceBitcoinSupported(): boolean {
	if (typeof window === "undefined") return false;

	try {
		// 只有 App 内浏览器支持 Bitcoin
		if ((window as any).binancew3w?.bitcoin) return true;
		if ((window as any).unisat?.isBinance) return true;
		return false;
	} catch (error) {
		return false;
	}
}

/**
 * 获取钱包下载链接
 */
export function getWalletDownloadUrl(walletType: string): {
	ios: string;
	android: string;
} {
	const downloadUrls = {
		okx: {
			ios: "https://apps.apple.com/app/okx-buy-bitcoin-btc-crypto/id1327268470",
			android:
				"https://play.google.com/store/apps/details?id=com.okinc.okex.gp",
		},
		binance: {
			ios: "https://apps.apple.com/app/binance-buy-bitcoin-crypto/id1436799971",
			android: "https://play.google.com/store/apps/details?id=com.binance.dev",
		},
		unisat: {
			ios: "https://apps.apple.com/app/unisat-wallet/id1642969156",
			android:
				"https://play.google.com/store/apps/details?id=com.unisat.wallet",
		},
		xverse: {
			ios: "https://apps.apple.com/app/xverse-wallet/id1552578449",
			android:
				"https://play.google.com/store/apps/details?id=com.secretkeylabs.xverse",
		},
		phantom: {
			ios: "https://apps.apple.com/app/phantom-crypto-wallet/id1598432977",
			android: "https://play.google.com/store/apps/details?id=app.phantom",
		},
		tokenpocket: {
			ios: "https://apps.apple.com/app/tokenpocket-btc-crypto-wallet/id1436028697",
			android: "https://play.google.com/store/apps/details?id=com.tokenpocket",
		},
	} as const;

	return (
		downloadUrls[walletType as keyof typeof downloadUrls] || {
			ios: "",
			android: "",
		}
	);
}

/**
 * 处理移动端钱包连接流程
 * 统一处理深链接跳转和错误提示
 */
export async function handleMobileWalletConnection(
	walletType: string,
	onSuccess?: () => void,
	onError?: (error: string) => void,
	options: {
		dappUrl?: string;
	} = {},
): Promise<boolean> {
	if (!isMobileDevice()) {
		return true; // PC端直接返回成功
	}

	const mobileEnv = getMobileWalletEnvironment();
	const walletNames = {
		okx: "OKX Wallet",
		unisat: "UniSat Wallet",
		xverse: "Xverse Wallet",
		phantom: "Phantom Wallet",
		tokenpocket: "TokenPocket Wallet",
	};

	const walletName =
		walletNames[walletType as keyof typeof walletNames] || walletType;

	uiLog.info(`📱 处理${walletName}移动端连接...`);

	// 如果已经在目标钱包浏览器中，直接返回成功
	if (mobileEnv.walletType === walletType) {
		uiLog.info(`✅ 已在${walletName}浏览器中`);
		return true;
	}

	// 如果可以直接连接（有注入对象），返回成功
	if (canConnectWallet(walletType)) {
		uiLog.info(`✅ ${walletName}可直接连接`);
		return true;
	}

	// 尝试深链接跳转
	uiLog.info(`🔗 尝试通过深链接打开${walletName}...`);

	const success = await openWalletDeepLink(walletType, () => {
		// 深链接失败的回调
		const { ios, android } = getWalletDownloadUrl(walletType);
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
		const downloadUrl = isIOS ? ios : android;

		const errorMessage = downloadUrl
			? `请在${walletName}中打开此页面，或下载${walletName}:\n${downloadUrl}`
			: `请在${walletName}中打开此页面`;

		if (onError) {
			onError(errorMessage);
		}
	}, {
		dappUrl: options.dappUrl,
	});

	if (success && onSuccess) {
		onSuccess();
	}

	return success;
}
